import { useState, useEffect } from 'react'
import { lumi } from '../lib/lumi'
import { useAuth } from '../hooks/useAuth'
import AnalyticsFilters from '../components/analytics/AnalyticsFilters'
import AnalyticsOverviewTab from '../components/analytics/AnalyticsOverviewTab'
import CohortAnalysisTab from '../components/analytics/CohortAnalysisTab'
import RFMSegmentationTab from '../components/analytics/RFMSegmentationTab'
import BehavioralSegmentationTab from '../components/analytics/BehavioralSegmentationTab'
import AdvancedSegmentsTab from '../components/analytics/AdvancedSegmentsTab'

interface Campaign {
  _id: string
  subject: string
  sentCount: number
  openCount: number
  clickCount: number
  sentAt: string
}

interface AnalyticsEvent {
  campaignId: string
  subscriberEmail: string
  eventType: 'open' | 'click'
  timestamp: string
  userAgent?: string
  device?: string
  location?: string
  emailClient?: string
}

export default function AdminAnalytics() {
  const { user } = useAuth()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState('30')
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all')
  const [segmentBy, setSegmentBy] = useState<'device' | 'location' | 'emailClient' | 'cohort'>('device')
  const [subscribers, setSubscribers] = useState<any[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'cohort' | 'rfm' | 'behavioral' | 'segments'>('overview')

  useEffect(() => {
    if (user?.userRole === 'admin') {
      fetchData()
    }
  }, [user, dateRange])

  const fetchData = async () => {
    setLoading(true)
    try {
      const daysAgo = parseInt(dateRange)
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo)

      const [campaignsRes, analyticsRes, subscribersRes] = await Promise.all([
        lumi.entities.newsletterCampaigns.list({
          filter: { status: 'sent', sentAt: { $gte: cutoffDate.toISOString() } },
          sort: { sentAt: -1 }
        }),
        lumi.entities.newsletterAnalytics.list({
          filter: { timestamp: { $gte: cutoffDate.toISOString() } },
          limit: 10000
        }),
        lumi.entities.newsletterSubscribers.list({
          filter: { status: 'active' },
          limit: 10000
        })
      ])

      setCampaigns(campaignsRes.list || [])
      setAnalytics(analyticsRes.list || [])
      setSubscribers(subscribersRes.list || [])
    } catch (error) {
      console.error('Analytics fetch failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const getAdvancedMetrics = () => {
    const totalSent = campaigns.reduce((sum, c) => sum + c.sentCount, 0)
    const totalOpened = campaigns.reduce((sum, c) => sum + c.openCount, 0)
    const totalClicked = campaigns.reduce((sum, c) => sum + c.clickCount, 0)
    
    const avgOpenRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0
    const avgClickRate = totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0
    const avgCTR = totalSent > 0 ? (totalClicked / totalSent) * 100 : 0
    
    const engagementScore = Math.round(
      (avgOpenRate * 0.4) + (avgClickRate * 0.4) + (avgCTR * 0.2)
    )

    return {
      totalSent,
      totalOpened,
      totalClicked,
      avgOpenRate: avgOpenRate.toFixed(2),
      avgClickRate: avgClickRate.toFixed(2),
      avgCTR: avgCTR.toFixed(2),
      engagementScore
    }
  }

  if (!user || user.userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Access denied. Admin only.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    )
  }

  const metrics = getAdvancedMetrics()

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Advanced Newsletter Analytics</h1>
          <p className="text-gray-600">Deep insights and predictive analytics for your campaigns</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm p-2 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'overview' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('cohort')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'cohort' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Cohort Analysis
            </button>
            <button
              onClick={() => setActiveTab('rfm')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'rfm' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              RFM Segmentation
            </button>
            <button
              onClick={() => setActiveTab('behavioral')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'behavioral' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Behavioral
            </button>
            <button
              onClick={() => setActiveTab('segments')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'segments' ? 'bg-green-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Advanced Segments
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnalyticsFilters
          dateRange={dateRange}
          setDateRange={setDateRange}
          selectedCampaign={selectedCampaign}
          setSelectedCampaign={setSelectedCampaign}
          segmentBy={segmentBy}
          setSegmentBy={setSegmentBy}
          campaigns={campaigns}
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <AnalyticsOverviewTab
            campaigns={campaigns}
            analytics={analytics}
            metrics={metrics}
          />
        )}

        {activeTab === 'cohort' && (
          <CohortAnalysisTab
            campaigns={campaigns}
            analytics={analytics}
          />
        )}

        {activeTab === 'rfm' && (
          <RFMSegmentationTab
            analytics={analytics}
          />
        )}

        {activeTab === 'behavioral' && (
          <BehavioralSegmentationTab
            analytics={analytics}
          />
        )}

        {activeTab === 'segments' && (
          <AdvancedSegmentsTab />
        )}

        {/* Export & Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6 mt-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Export & Actions</h2>
          <div className="flex flex-wrap gap-4">
            <button className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700">
              Export to CSV
            </button>
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700">
              Export to PDF
            </button>
            <button className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700">
              Schedule Report
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
